import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Package,
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  User,
} from "lucide-react";
import Button from "shared/Button";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import {
  getSkuImports,
  updateInventoryStatus,
  InventoryItem,
} from "core/Services/InventoryServices";
import { getStatusColors } from "utils/statusColors";

const SkuImportDetails = () => {
  const { t } = useTranslation();
  const { merchantId, sku } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [imports, setImports] = useState<InventoryItem[]>([]);
  const [selectedImport, setSelectedImport] = useState<InventoryItem | null>(
    null
  );
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [missingQuantity, setMissingQuantity] = useState<string>("");

  useEffect(() => {
    const fetchImports = async () => {
      if (!merchantId || !sku) return;

      setIsLoading(true);
      try {
        const response = await getSkuImports(parseInt(merchantId), sku);
        setImports(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching SKU imports:", error);
        toast.error(t("inventory.errorFetchingImports"));
        setImports([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchImports();
  }, [merchantId, sku, t]);

  const handleUpdateStatus = (importRecord: InventoryItem) => {
    setSelectedImport(importRecord);
    setSelectedStatus(importRecord.quantity_status);
    setMissingQuantity(importRecord.missing_quantity_number?.toString() || "");
    setShowStatusModal(true);
  };

  const handleSaveStatus = async () => {
    if (!selectedImport || !selectedStatus) return;

    // Validate missing quantity if status is missing_quantity
    if (selectedStatus === "missing_quantity") {
      const qty = parseInt(missingQuantity);
      if (!missingQuantity || isNaN(qty) || qty <= 0) {
        toast.error(
          t("inventory.enterValidQuantity") || "Please enter a valid quantity"
        );
        return;
      }
      if (qty >= selectedImport.quantity) {
        toast.error(
          t("inventory.missingQuantityTooHigh") ||
            "Missing quantity cannot be greater than or equal to total quantity"
        );
        return;
      }
    }

    try {
      await updateInventoryStatus(selectedImport.id, {
        quantity_status: selectedStatus as
          | "under_review"
          | "received"
          | "missing_quantity",
        missing_quantity_number:
          selectedStatus === "missing_quantity"
            ? parseInt(missingQuantity)
            : undefined,
      });

      toast.success(
        t("inventory.statusUpdateSuccess") || "Status updated successfully!"
      );
      setShowStatusModal(false);
      setSelectedImport(null);

      // Refresh imports
      const response = await getSkuImports(parseInt(merchantId!), sku!);
      setImports(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(t("inventory.statusUpdateError") || "Error updating status");
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = getStatusColors(status);
    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}
      >
        {status === "under_review"
          ? t("inventory.underReview")
          : status === "received"
          ? t("inventory.received")
          : t("inventory.missingQuantity")}
      </span>
    );
  };

  const totalApprovedQuantity = imports.reduce((sum, imp) => {
    return sum + (imp.available_quantity || 0);
  }, 0);

  const pendingCount = imports.filter(
    (imp) => imp.quantity_status === "under_review"
  ).length;

  return (
    <div className="flex gap-4 flex-col p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="bg-white rounded-md shadow-sm p-4 sm:p-6 flex gap-4 flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/inventory/${merchantId}`)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={t("common.back")}
            >
              <ArrowLeft size={24} />
            </button>
            <Package size={32} className="text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {t("inventory.skuImports")}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                SKU: <span className="font-mono font-semibold">{sku}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {!isLoading && imports.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-600 font-medium">
                {t("inventory.totalImports")}
              </p>
              <p className="text-2xl font-bold text-blue-900 mt-1">
                {imports.length}
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-green-600 font-medium">
                {t("inventory.approvedQuantity")}
              </p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {totalApprovedQuantity}
              </p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-600 font-medium">
                {t("inventory.pendingApproval")}
              </p>
              <p className="text-2xl font-bold text-yellow-900 mt-1">
                {pendingCount}
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-600 font-medium">
                {t("inventory.productName")}
              </p>
              <p className="text-lg font-bold text-purple-900 mt-1 truncate">
                {imports[0]?.product_name || "-"}
              </p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <p className="text-sm text-indigo-600 font-medium">
                {t("inventory.manufacturer")}
              </p>
              <p className="text-lg font-bold text-indigo-900 mt-1 truncate">
                {imports[0]?.manufacturer || "-"}
              </p>
            </div>
          </div>
        )}

        {/* Import Records Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.importDate")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.importedBy")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.quantity")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.price")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.status")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.missingQty")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.availableQty")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.note")}
                </th>
                <th className="text-right p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                Array(3)
                  .fill(0)
                  .map((_, index) => (
                    <tr key={index}>
                      <td className="py-4 px-3">
                        <Skeleton width={120} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={100} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={80} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={40} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={100} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={80} />
                      </td>
                    </tr>
                  ))
              ) : imports.length > 0 ? (
                imports.map((importRecord) => (
                  <tr
                    key={importRecord.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-4 px-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar size={14} />
                        {new Date(importRecord.import_date).toLocaleString()}
                      </div>
                    </td>
                    <td className="py-4 px-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <User size={14} />
                        {importRecord.imported_by?.name || "-"}
                      </div>
                    </td>
                    <td className="py-4 px-3">
                      <span className="font-semibold text-gray-900">
                        {importRecord.quantity}
                      </span>
                    </td>
                    <td className="py-4 px-3 text-gray-600">
                      JOD {importRecord.price.toFixed(2)}
                    </td>
                    <td className="py-4 px-3">
                      {getStatusBadge(importRecord.quantity_status)}
                    </td>
                    <td className="py-4 px-3">
                      {importRecord.quantity_status === "missing_quantity" ? (
                        <span className="text-red-600 font-medium">
                          {importRecord.missing_quantity_number}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-4 px-3">
                      <span
                        className={`font-semibold ${
                          importRecord.available_quantity > 0
                            ? "text-green-600"
                            : "text-gray-400"
                        }`}
                      >
                        {importRecord.available_quantity}
                      </span>
                    </td>
                    <td className="py-4 px-3">
                      <span className="text-sm text-gray-600 truncate max-w-[150px] block">
                        {importRecord.note || "-"}
                      </span>
                    </td>
                    <td className="py-4 px-3 text-right">
                      <Button
                        onClick={() => handleUpdateStatus(importRecord)}
                        className="text-sm px-3 py-1"
                        variant="outlined"
                      >
                        {t("inventory.updateStatus")}
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={9}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    {t("inventory.noImportsFound")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && selectedImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {t("inventory.updateStatus")}
            </h2>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">SKU:</span> {selectedImport.sku}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">{t("inventory.quantity")}:</span>{" "}
                {selectedImport.quantity}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">
                  {t("inventory.importDate")}:
                </span>{" "}
                {new Date(selectedImport.import_date).toLocaleDateString()}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("inventory.selectStatus")}
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="under_review">
                  {t("inventory.underReview")}
                </option>
                <option value="received">{t("inventory.received")}</option>
                <option value="missing_quantity">
                  {t("inventory.missingQuantity")}
                </option>
              </select>
            </div>

            {selectedStatus === "missing_quantity" && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("inventory.missingQuantityNumber")}
                </label>
                <input
                  type="number"
                  min="1"
                  max={selectedImport.quantity - 1}
                  value={missingQuantity}
                  onChange={(e) => setMissingQuantity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={t("inventory.enterMissingQuantity")}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t("inventory.availableAfterMissing")}:{" "}
                  {selectedImport.quantity} - {missingQuantity || 0} ={" "}
                  {selectedImport.quantity - (parseInt(missingQuantity) || 0)}
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedImport(null);
                }}
                variant="outlined"
              >
                {t("common.cancel")}
              </Button>
              <Button onClick={handleSaveStatus}>{t("common.save")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkuImportDetails;
